import * as React from 'react';
import styled from 'styled-components';
import cl from 'classnames';

import { innerBorder } from '@doltech/utils/lib/css-style';
import { colorsV2 } from '../colors-v2';

const ChipsContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0 6px;
`;

const ChipsMain = styled.div<{ activeColor?: string; bgColor?: string }>`
  display: flex;
  align-items: center;

  background-color: ${(p) => p.bgColor || 'transparent'};
  color: ${colorsV2.neutral180};
  ${innerBorder(`1px solid ${colorsV2.neutral20}`)}
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;

  &.has-click-action {
    cursor: pointer;
    user-select: none;
  }

  &.size-small {
    padding: 8px 12px;
    border-radius: 12px;
  }

  &.size-large {
    padding: 12px;
    border-radius: 16px;
  }

  &.with-hover {
    &:hover:not(.disabled) {
      background-color: ${colorsV2.gray20};
    }
  }

  &.disabled {
    background-color: transparent;
    color: ${colorsV2.gray60};
    &.has-click-action {
      cursor: default;
    }
  }

  &.type-on {
    background-color: ${(p) => p.activeColor};
    color: ${colorsV2.white100};
    ${innerBorder('none')}

    &.with-hover {
      &:hover:not(.disabled) {
        opacity: 0.8;
      }
    }

    &.disabled {
      background-color: ${colorsV2.gray10};
      color: ${colorsV2.gray100};
    }
  }
`;

export interface ChipsProps
  extends Pick<React.HTMLAttributes<HTMLDivElement>, 'className' | 'children' | 'onClick'> {
  size?: 'small' | 'large';
  type?: 'on' | 'off';
  activeColor?: string;
  bgColor?: string;
  hover?: boolean;
  disabled?: boolean;
}

export const Chips = (props: ChipsProps) => {
  const {
    className,
    children,
    size = 'large',
    type = 'off',
    activeColor = colorsV2.blue100,
    bgColor,
    onClick,
    hover: withHover = false,
    disabled = false,
  } = props;
  return (
    <ChipsMain
      activeColor={activeColor}
      bgColor={bgColor}
      className={cl(
        'chips',
        `size-${size} type-${type}`,
        {
          'has-click-action': Boolean(onClick),
          'with-hover': withHover,
          disabled,
        },
        className
      )}
      onClick={(e) => !disabled && onClick?.(e)}
    >
      <ChipsContent>
        {children}
      </ChipsContent>
    </ChipsMain>
  );
};
